import type {
  Workspace, Account, Task, Assignee, Automation, AutomationCondition, ConditionOp, RoleDef,
} from "./types";

/* Client mirror of the server automation engine (api/src/automation/engine.ts).
 * Pure functions over a workspace snapshot — used for the offline demo's preview
 * and "run"; when the API is live the server engine runs instead, and the two
 * produce the same tasks. Anchored to a fixed "today" for a deterministic demo. */

export const TODAY = "2026-08-17";

export interface TaskInput {
  title: string;
  description?: string;
  accountId: string;
  assignee: Assignee;
  due: string;
  status?: Task["status"];
  source?: Task["source"];
  priority?: Task["priority"];
  automationId?: string;
  typeId?: string;
  fields?: Task["fields"];
}
export interface ProposedTask {
  input: TaskInput;
  automationId: string;
  automationName: string;
}

export function accountFields(a: Account): Record<string, string | number | boolean> {
  const flat: Record<string, string | number | boolean> = {
    health: a.health, arr: a.arr, seats: a.seats, seatsActive: a.seatsActive,
    region: a.region, industry: a.industry, lifecycleStage: a.lifecycleStage,
    healthBand: a.healthBand, expansionPotential: a.expansionPotential,
    renewalDate: a.renewalDate, ownerId: a.ownerId, name: a.name,
  };
  for (const [k, v] of Object.entries(a.attributes ?? {})) flat[k] = v;
  return flat;
}

const asNum = (v: unknown): number => (typeof v === "number" ? v : Number(v));

export function testCondition(op: ConditionOp, left: unknown, right: unknown): boolean {
  switch (op) {
    case "lt": return asNum(left) < asNum(right);
    case "lte": return asNum(left) <= asNum(right);
    case "gt": return asNum(left) > asNum(right);
    case "gte": return asNum(left) >= asNum(right);
    case "eq": return String(left) === String(right);
    case "neq": return String(left) !== String(right);
    case "contains": return String(left ?? "").toLowerCase().includes(String(right ?? "").toLowerCase());
    default: return false;
  }
}
const matchesConditions = (a: Account, cs: AutomationCondition[]): boolean => {
  const flat = accountFields(a);
  return cs.every((c) => testCondition(c.op, flat[c.field], c.value));
};

const daysUntil = (d: string): number => {
  const a = Date.parse(d), t = Date.parse(TODAY);
  if (Number.isNaN(a) || Number.isNaN(t)) return Infinity;
  return Math.round((a - t) / 86_400_000);
};
const addDays = (d: string, n: number): string => new Date(Date.parse(d) + n * 86_400_000).toISOString().slice(0, 10);

export type RoleHolder =
  | { kind: "user"; userId: string; name: string }
  | { kind: "contact"; name: string; email?: string; title?: string; contactRole?: string }
  | { kind: "none" };

const userHolder = (ws: Workspace, userId: unknown): RoleHolder | null => {
  if (typeof userId !== "string") return null;
  const u = ws.users.find((x) => x.id === userId);
  return u ? { kind: "user", userId: u.id, name: u.name } : null;
};

/** Who holds a role on an account — a user, an external contact, or none.
 * Follows the role's mapping first, then the manual account team. */
export function resolveRoleHolder(ws: Workspace, accountId: string, roleKey: string): RoleHolder {
  const account = ws.accounts.find((a) => a.id === accountId);
  const role = ws.roles.find((r) => r.key === roleKey);
  const m = role?.mapping;
  if (m?.type === "account_field" && account) { const u = userHolder(ws, accountFields(account)[m.field]); if (u) return u; }
  if (m?.type === "contact_role" && account) {
    const c = account.contacts.find((x) => x.role === m.contactRole);
    if (c) return { kind: "contact", name: c.name, email: c.email, title: c.title, contactRole: c.role };
  }
  if (m?.type === "record_ref") {
    const row = (ws.records?.[m.object] ?? []).find((r) => String(r.attributes[m.matchField]) === accountId);
    if (row) { const u = userHolder(ws, row.attributes[m.holderField]); if (u) return u; }
  }
  const team = ws.teams.find((t) => t.accountId === accountId);
  const teamed = userHolder(ws, role ? team?.roles[role.key] : undefined);
  return teamed ?? { kind: "none" };
}

export function resolveAssignee(ws: Workspace, accountId: string, assignee: Assignee): string {
  if (assignee.kind === "user") return assignee.userId;
  const holder = resolveRoleHolder(ws, accountId, assignee.role);
  if (holder.kind === "user") return holder.userId;
  const account = ws.accounts.find((a) => a.id === accountId);
  return account?.ownerId || ws.currentUserId || ws.users[0]?.id || "";
}
export function roleLabel(roles: RoleDef[], key: string): string {
  return roles.find((r) => r.key === key)?.label ?? key;
}
export function assigneeLabel(ws: Workspace, assignee: Assignee): string {
  if (assignee.kind === "user") return ws.users.find((u) => u.id === assignee.userId)?.name ?? "Unknown";
  return `${roleLabel(ws.roles, assignee.role)} (role)`;
}

function candidates(ws: Workspace, a: Automation): { account: Account; signal?: string }[] {
  const { type, params = {} } = a.trigger;
  const byId = (id: string) => ws.accounts.find((x) => x.id === id);
  switch (type) {
    case "health_below": {
      const threshold = asNum(params.threshold ?? 45);
      return ws.accounts.filter((x) => x.health < threshold).map((account) => ({ account }));
    }
    case "renewal_within": {
      const days = asNum(params.days ?? 90);
      return ws.accounts.filter((x) => { const d = daysUntil(x.renewalDate); return d >= 0 && d <= days; }).map((account) => ({ account }));
    }
    case "health_band_is": {
      const band = String(params.band ?? "risk");
      return ws.accounts.filter((x) => x.healthBand === band).map((account) => ({ account }));
    }
    case "signal_created": {
      const band = params.band ? String(params.band) : null;
      return ws.signals
        .filter((s) => s.status !== "resolved" && (!band || s.band === band))
        .map((s) => ({ account: byId(s.accountId), signal: s.title }))
        .filter((c): c is { account: Account; signal: string } => !!c.account);
    }
    default: return [];
  }
}

const fill = (tpl: string, vars: Record<string, string>): string => tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
const alreadyOpen = (existing: Task[], automationId: string, accountId: string): boolean =>
  existing.some((t) => t.automationId === automationId && t.accountId === accountId && t.status !== "done");

export function proposeTasks(ws: Workspace, existing: Task[], onlyId?: string): ProposedTask[] {
  const out: ProposedTask[] = [];
  const rules = ws.automations.filter((a) => (onlyId ? a.id === onlyId : a.enabled));
  for (const a of rules) {
    for (const { account, signal } of candidates(ws, a)) {
      if (!matchesConditions(account, a.conditions)) continue;
      if (alreadyOpen(existing, a.id, account.id)) continue;
      for (const act of a.actions) {
        out.push({
          automationId: a.id, automationName: a.name,
          input: {
            title: fill(act.title, { account: account.name, signal: signal ?? "" }),
            accountId: account.id, assignee: act.assignee, due: addDays(TODAY, act.dueInDays),
            status: "todo", source: "automation", priority: act.priority, automationId: a.id,
            typeId: act.taskTypeId || undefined,
          },
        });
      }
    }
  }
  return out;
}

/** Build a full Task from a proposed input (used by the offline "run"). */
export function materialize(ws: Workspace, input: TaskInput, id: string): Task {
  const account = ws.accounts.find((a) => a.id === input.accountId);
  return {
    id, title: input.title, description: input.description,
    accountId: input.accountId, accountName: account?.name ?? "",
    assignee: input.assignee, ownerId: resolveAssignee(ws, input.accountId, input.assignee),
    due: input.due, status: input.status ?? "todo", source: input.source ?? "manual",
    priority: input.priority ?? "medium", automationId: input.automationId,
    typeId: input.typeId, fields: input.fields, createdAt: TODAY,
  };
}
